import { Handle, useNodeConnections } from "@xyflow/react";
import { Position } from "@xyflow/react";
import {
  LogicalOperator,
  Outcome,
} from "@unlikelyai-magic/shared/data-access/api";
import { cn } from "@unlikelyai-magic/utils";
import { LogicalOperatorPill } from "../LogicalOperatorPill";
import "./BaseNode.scss";

/**
 * Base data type for nodes that can be rendered with BaseNode.
 * More flexible than RFNodeData - only requires the fields actually used by BaseNode.
 * Named BaseNodeRenderData to avoid collision with BaseNodeData in types.ts.
 */
export interface BaseNodeRenderData {
  direction?: "TB" | "LR";
  logicalOperator?: LogicalOperator;
  isInternallyEvaluated?: boolean;
  [key: string]: unknown;
}

export const BaseNode = ({
  id,
  data,
  isConnectable,
  outcome,
  children,
}: {
  id: any;
  data: BaseNodeRenderData;
  isConnectable: boolean;
  outcome: Outcome;
  children: React.ReactNode;
}) => {
  const incomingConnections = useNodeConnections({
    handleType: "source",
  });
  const outgoingConnections = useNodeConnections({
    handleType: "target",
  });
  const isLeafNode: boolean = incomingConnections.length === 0;
  const isInternallyEvaluated: boolean = data.isInternallyEvaluated ?? false;

  const borderColor =
    outcome === Outcome.TRUE
      ? "border-green-500"
      : outcome === Outcome.FALSE
        ? "border-red-500"
        : outcome === Outcome.UNKNOWN
          ? "border-orange-500"
          : "border-zinc-300";

  // Base background color based on outcome
  const baseBackgroundColor =
    outcome === Outcome.TRUE
      ? "bg-green-500"
      : outcome === Outcome.FALSE
        ? "bg-red-500"
        : outcome === Outcome.UNKNOWN
          ? "bg-orange-500"
          : isLeafNode
            ? "bg-white"
            : "bg-white node-striped-background";

  // If internally evaluated and undecided, use a slightly grey tint
  const backgroundColor =
    outcome === Outcome.UNDECIDED && isInternallyEvaluated
      ? "bg-zinc-100"
      : baseBackgroundColor;

  return (
    <div
      data-node-id={id}
      className={cn(
        "relative w-80 py-3 px-4 text-center border rounded-lg items-center shadow-sm",
        backgroundColor,
        borderColor
      )}
    >
      {/* Icon for internally evaluated nodes */}
      {isInternallyEvaluated && (
        <div
          className="absolute top-1 right-1 text-zinc-400"
          title="Internally evaluated (not shown to user)"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="currentColor"
            className="w-4 h-4"
            aria-label="Internally evaluated node"
          >
            <path d="M3.53 2.47a.75.75 0 0 0-1.06 1.06l18 18a.75.75 0 1 0 1.06-1.06l-18-18ZM22.676 12.553a11.249 11.249 0 0 1-2.631 4.31l-3.099-3.099a5.25 5.25 0 0 0-6.71-6.71L7.759 4.577a11.217 11.217 0 0 1 4.242-.827c4.97 0 9.185 3.223 10.675 7.69.12.362.12.752 0 1.113Z" />
            <path d="M15.75 12c0 .18-.013.357-.037.53l-4.244-4.243A3.75 3.75 0 0 1 15.75 12ZM12.53 15.713l-4.243-4.244a3.75 3.75 0 0 0 4.244 4.243Z" />
            <path d="M6.75 12c0-.619.107-1.213.304-1.764l-3.1-3.1a11.25 11.25 0 0 0-2.63 4.31c-.12.362-.12.752 0 1.114 1.489 4.467 5.704 7.69 10.675 7.69 1.5 0 2.933-.294 4.242-.827l-2.477-2.477A5.25 5.25 0 0 1 6.75 12Z" />
          </svg>
        </div>
      )}
      {outgoingConnections.length !== 0 && (
        <Handle
          type="target"
          position={data.direction === "LR" ? Position.Left : Position.Top}
          isConnectable={isConnectable}
        />
      )}
      {children}
      {incomingConnections.length !== 0 && (
        <Handle
          type="source"
          position={data.direction === "LR" ? Position.Right : Position.Bottom}
          isConnectable={isConnectable}
        >
          {data.logicalOperator && (
            <LogicalOperatorPill
              operator={data.logicalOperator}
              className={"translate-y-2 -translate-x-1/2 select-none"}
            />
          )}
        </Handle>
      )}
    </div>
  );
};
